import { Grid } from '@mui/material'
import { Formik } from 'formik'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react' // changed: add useRef, remove useMemo
import { useTranslation } from 'react-i18next'
import { getLocalizedRoleName } from '../../../../common/roleHelper'
import { getErrMsg, toastNeg, toastPos } from '../../../../common/toastHelper'
import {
  IEditUserProject,
  ILinkedSkill,
  INewEmployer,
  INewProject,
  IOtherEmployerDetailsForm,
  IPrivateProjectFormPackage,
  IUserProject,
} from '../../../../interfaces/projects/IProjects'
import { ISkillProject } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import DeleteConfirmation from '../../../OPcomponents/DeleteConfirmation'
import { ExitConfirmation } from '../../../OPcomponents/ExitConfirmation'
import AutoSave from '../../../OPcomponents/FormikAutosave'
import { employerJobTitlesWorkHistorySchema } from '../../../OPcomponents/ValidationSchemas'
import { OPSkillsButtons } from '../../OPSkillsButtons'
import NewPersonalEmployerJobTitlesProject from './NewPersonalEmployerJobTitlesProject'

interface IAddNewPrivateProject {
  isProjectSite?: boolean
  isCreateNewProject?: boolean
  isEdit?: boolean
  collapseHandler?: () => void
  data?: IPrivateProjectFormPackage
}

export interface initValues {
  employer: IOtherEmployerDetailsForm
  project: INewProject
  userProject: IUserProject
}

const AddNewEmployerJobTitlesProject = (props: IAddNewPrivateProject): JSX.Element => {
  const { isProjectSite = false, isCreateNewProject = false, isEdit = false, collapseHandler, data } = props
  const { t } = useTranslation()
  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore
  const {
    newEmployerName,
    getAllEmployerDetails,
    allOtherEmployerDetails,
    postEmployer,
    postProject,
    postUserProject,
    editUserOwnedProject,
    editEmployer,
    deleteUserProject,
    getAllProjects,
    showAddEmployerJobTitlesProjectModalHandler,
    showModalHandler,
    getAllEmployers,
  } = rootStore.projectStore

  const { postNewSkillProjectLinks, deleteNewSkillProjectLinks } = rootStore.skillStore

  const { getUserEmployersJobTitlesWorkHistory } = rootStore.personStore

  const [projectSkills, setProjectSkills] = useState<ISkillProject[]>([])
  const [deleteProjectConfirmation, setDeleteProjectConfirmation] = useState(false)

  // A ref that keeps the last-saved baseline for diffing.
  const baselineLinkedSkillsRef = useRef<ISkillProject[]>([])

  // Initialize both projectSkills and baseline from incoming data.
  useEffect(() => {
    const links: ISkillProject[] = []
    if (data?.userDetails.linkedSkills) {
      data.userDetails.linkedSkills.forEach((skill: ILinkedSkill) => {
        links.push({
          userId: validatedUser,
          projectId: data?.project.projectId,
          skillId: skill.skillId,
          textFi: skill.skillNameFi,
          textEng: skill.skillNameEng,
        })
      })
    }
    setProjectSkills(links)
    baselineLinkedSkillsRef.current = links
  }, [data?.project.projectId, data?.userDetails.linkedSkills, validatedUser])

  const linksModified = _.isEqual(_.sortBy(projectSkills, ['skillId']), _.sortBy(baselineLinkedSkillsRef.current, ['skillId']))

  const initValues: initValues = {
    employer: {
      name: data?.employerDetails.name || newEmployerName || '',
      userId: validatedUser,
      jobTitle: data?.employerDetails.jobTitle || '',
      jobDescriptionFi: data?.employerDetails.jobDescriptionFi || '',
      jobDescriptionEng: data?.employerDetails.jobDescriptionEng || '',
      startDate: data?.employerDetails.startDate || undefined,
      endDate: data?.employerDetails.endDate || undefined,
      employerId: data?.employerDetails.employerId || -1,
    },
    project: {
      userId: validatedUser,
      name: data?.project.name || '',
      branchId: data?.project.branchId || -1,
      description: data?.project.description || '',
      customer: data?.project.customer || '',
      public: data?.project.public || false,
      isNetum: false,
      canUseAsReference: data?.project.canUseAsReference || false,
      employerId: data?.project.employerId ?? -1,
    },
    userProject: {
      userId: validatedUser,
      projectId: data?.userDetails.projectId || -1,
      startDate: data?.userDetails.startDate || data?.employerDetails.startDate || undefined,
      endDate: data?.userDetails.endDate || undefined,
      manDays: (() => {
        const rawManDays = data?.userDetails.manDays || 0
        const rawManMonths = data?.userDetails.manMonths || 0
        const rawUsingManDays = data?.userDetails.usingManDays ?? true

        if (rawUsingManDays && rawManDays === 0 && rawManMonths > 0) {
          return Math.round(rawManMonths * 21)
        }
        return rawManDays
      })(),
      manMonths: (() => {
        const rawManDays = data?.userDetails.manDays || 0
        const rawManMonths = data?.userDetails.manMonths || 0
        const rawUsingManDays = data?.userDetails.usingManDays ?? true

        if (!rawUsingManDays && rawManMonths === 0 && rawManDays > 0) {
          return Math.round(rawManDays / 21)
        }
        return rawManMonths
      })(),
      usingManDays: data?.userDetails.usingManDays ?? true,
      usingManMonths: data?.userDetails.usingManMonths ?? false,
      descriptionFi: data?.userDetails.descriptionFi || '',
      descriptionEng: data?.userDetails.descriptionEng || '',
      roleIds:
        data?.userDetails.roles.map((role) => ({ value: role.roleId.toString(), key: role.roleId, text: getLocalizedRoleName(role) })) ||
        [],
      isKeyProject: data?.userDetails.isKeyProject ?? true,
    },
  }

  useEffect(() => {
    if (allOtherEmployerDetails.length === 0) {
      getAllEmployerDetails(validatedUser)
    }
  }, [allOtherEmployerDetails.length, getAllEmployerDetails, validatedUser])

  const handleSubmitNew = async (values: initValues) => {
    try {
      if (values.employer.employerId === -1 && values.project.employerId === -1) {
        const newEmp = await postEmployer(values.employer as INewEmployer)
        values.project.employerId = newEmp
      }
      values.project.description = values.employer.jobDescriptionFi || ''
      const projId = await postProject(values.project)

      const personProject: IEditUserProject = {
        userId: validatedUser,
        projectId: projId,
        startDate: values.employer.startDate,
        endDate: values.employer.endDate,
        workAmount: values.userProject.usingManDays ? values.userProject.manDays : values.userProject.manMonths,
        usingManDays: !!values.userProject.usingManDays,
        usingManMonths: !values.userProject.usingManDays,
        descriptionFi: values.employer.jobDescriptionFi,
        descriptionEng: values.employer.jobDescriptionEng,
        isEmployerJobTitlesProject: true,
        roleIds: values.userProject.roleIds.map((role) => role.key) || [],
        isKeyProject: values.userProject.isKeyProject,
      }

      await postUserProject(personProject)
      if (projectSkills.length > 0) {
        const toPost = projectSkills.map((skill) => ({ ...skill, projectId: projId }))
        await postNewSkillProjectLinks(toPost)
      }
      // ensure table refreshes reliably
      await getUserEmployersJobTitlesWorkHistory(validatedUser, false)
      await getAllEmployerDetails(validatedUser, false)
      await getAllEmployers(validatedUser)
      toastPos(t('Toasts.EmploymentCreate'))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.EmploymentCreateError'))
    } finally {
      handleClose()
    }
  }

  const handleSubmitEdit = async (values: initValues) => {
    try {
      // No new employer creation allowed
      if (!values.employer.employerId || !values.project.employerId) {
        toastNeg(t('Forms.EmployerRequired') || 'Please select an employer')
        return
      }

      if (values.employer.employerId !== values.project.employerId) {
        values.project.employerId = values.employer.employerId
      }

      // Allow editing employer-related user details (job title/desc/dates)
      const editedEmployer: IOtherEmployerDetailsForm = {
        employerId: values.employer.employerId,
        name: values.employer.name,
        userId: validatedUser,
        jobTitle: values.employer.jobTitle,
        jobDescriptionFi: values.employer.jobDescriptionFi,
        jobDescriptionEng: values.employer.jobDescriptionEng,
        startDate: values.employer.startDate,
        endDate: values.employer.endDate,
      }
      await editEmployer(editedEmployer)

      const editedProject = {
        userId: validatedUser,
        projectId: values.userProject.projectId,
        name: '',
        projectDescription: values.project.description,
        customer: values.project.customer,
        branchId: values.project.branchId,
        employerId: values.project.employerId,
        canUseAsReference: values.project.canUseAsReference,
        startDate: values.employer.startDate,
        endDate: values.employer.endDate,
        public: values.project.public,
        workAmount: values.userProject.usingManDays ? values.userProject.manDays : values.userProject.manMonths,
        usingManDays: !!values.userProject.usingManDays,
        usingManMonths: !values.userProject.usingManDays,
        userDescriptionFi: values.employer.jobDescriptionFi,
        userDescriptionEng: values.employer.jobDescriptionEng,
        isEmployerJobTitlesProject: true,
        roleIds: values.userProject.roleIds.map((role) => role.key) || [],
        contactPerson: values.project.contactPerson,
        isKeyProject: values.userProject.isKeyProject,
      }

      // Diff against the last-saved baseline, not the initial props.
      const baseline = baselineLinkedSkillsRef.current
      const removedLinks = _.differenceBy(baseline, projectSkills, 'skillId')
      const addedLinks = _.differenceBy(projectSkills, baseline, 'skillId')

      await editUserOwnedProject(editedProject)
      if (removedLinks.length > 0) await deleteNewSkillProjectLinks(removedLinks)
      if (addedLinks.length > 0) await postNewSkillProjectLinks(addedLinks)

      await getUserEmployersJobTitlesWorkHistory(validatedUser, false)
      await getAllEmployerDetails(validatedUser, false)
      await getAllEmployers(validatedUser)

      baselineLinkedSkillsRef.current = projectSkills.map((s) => ({ ...s }))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectEditError'))
    }
  }

  const handleClose = async () => {
    showAddEmployerJobTitlesProjectModalHandler(false)
    setTimeout(async () => {
      await getAllProjects(validatedUser, false)
      await getUserEmployersJobTitlesWorkHistory(validatedUser, false)
      await getAllEmployerDetails(validatedUser, false)
    }, 500)
  }

  const deleteProject = async (id: number) => {
    try {
      await deleteUserProject(validatedUser, id, true)
      await getAllEmployers(validatedUser)
      // ensure table refresh after delete
      await getUserEmployersJobTitlesWorkHistory(validatedUser, false)
      await getAllEmployerDetails(validatedUser, false)
      await getAllEmployers(validatedUser)
      toastPos(t('Toasts.ProjectUserDelete'), true)
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.ProjectUserDeleteError'), true)
    } finally {
      handleClose()
    }
  }

  useEffect(() => {
    return () => {
      handleClose()
    }
  }, [])

  const submitHandler = (values: initValues) => (isEdit ? handleSubmitEdit(values) : handleSubmitNew(values))

  return (
    <Grid container rowSpacing={4} className={isProjectSite || isCreateNewProject ? '' : 'gridcontainer--formcontainer__itempaddings'}>
      <Formik initialValues={initValues} validationSchema={employerJobTitlesWorkHistorySchema} onSubmit={submitHandler}>
        {({ isValid, dirty, handleSubmit, isSubmitting, values }) => (
          <>
            <ExitConfirmation when={dirty} />
            <Grid item xs={12}>
              <NewPersonalEmployerJobTitlesProject
                isNewProject={!isEdit}
                deleteProjectConfirmation={deleteProjectConfirmation}
                setProjectSkills={setProjectSkills}
                projectSkills={projectSkills}
                isEdit={isEdit}
              />
            </Grid>
            <Grid xs={12} item>
              <OPSkillsButtons
                isDelete={isEdit}
                onDelete={() => setDeleteProjectConfirmation(true)}
                bothDisabled={isSubmitting || deleteProjectConfirmation}
                disabled={!isValid || (!dirty && linksModified) || isSubmitting}
                noPadding
                bottomPadding
                onSubmit={handleSubmit}
                onClose={() => {
                  showModalHandler(false)
                  showAddEmployerJobTitlesProjectModalHandler(false)
                }}
                onAutosave={isEdit}
              />
            </Grid>
            {isEdit && (
              <DeleteConfirmation
                open={deleteProjectConfirmation}
                whatToDelete='Project'
                deleteHandler={() => deleteProject(values.userProject.projectId)}
                handleClose={() => setDeleteProjectConfirmation(false)}
              />
            )}
            {isEdit && <AutoSave />}
          </>
        )}
      </Formik>
    </Grid>
  )
}
export default observer(AddNewEmployerJobTitlesProject)
